import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Home, ShieldCheck, Users, Sparkles, Target,
  Calculator, TrendingUp, Check, Crown, MapPin, CreditCard, FileText,
  HandshakeIcon, Lock, BarChart3, Clock, FileSignature, LineChart, Building2
} from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";

// Reference price per m² (MXN) for valuation demo — based on Findperty MVIP averages
const CITY_PRICE_PER_M2 = {
  cdmx: 55000, "ciudad de méxico": 55000,
  monterrey: 48000, guadalajara: 38000,
  queretaro: 35000, "san luis potosí": 28000,
  puebla: 27000, leon: 25000,
  merida: 26000, cancun: 42000,
  morelia: 22000, mazatlan: 31000,
  toluca: 32000, tijuana: 36000,
  hermosillo: 28000, chihuahua: 24000,
  saltillo: 26000, "puerto vallarta": 45000,
  default: 25000,
};

const TYPE_FACTOR = { casa: 1.0, departamento: 1.05, loft: 1.08, "town house": 0.95, terreno: 0.45 };
const CONDITION_FACTOR = { excelente: 1.10, "muy bueno": 1.05, bueno: 1.0, regular: 0.92, "necesita reparación": 0.80 };
const AGE_FACTOR = (age) => {
  if (age <= 5) return 1.05;
  if (age <= 10) return 1.0;
  if (age <= 20) return 0.95;
  if (age <= 40) return 0.88;
  return 0.78;
};

// Zone tier multiplier — Master Partners use this band as base classifier
const ZONE_FACTOR = {
  premium: { factor: 1.45, label: "Premium / Alta plusvalía", desc: "Zonas exclusivas con alta demanda y servicios de lujo." },
  alta: { factor: 1.20, label: "Residencial consolidada", desc: "Colonias con infraestructura madura y buena conectividad." },
  media: { factor: 1.00, label: "Media estándar", desc: "Zonas habitacionales bien ubicadas, demanda estable." },
  popular: { factor: 0.78, label: "Popular / Periférica", desc: "Zonas en desarrollo o de acceso económico." },
};

// Notable colonias / micro-mercados (factor adicional sobre la zona).
// Fallback estático — la lista real se carga desde el backend en tiempo de ejecución.
const FALLBACK_COLONIA_FACTOR = {
  "polanco": 1.35, "lomas de chapultepec": 1.40, "condesa": 1.28, "roma norte": 1.25,
  "providencia": 1.28, "puerta de hierro": 1.38, "san pedro garza garcía": 1.42,
  "juriquilla": 1.25, "altabrisa": 1.20, "puerto cancún": 1.40, "zona hotelera": 1.50,
};

const BENEFITS = [
  {
    icon: Target,
    title: "Exposición dirigida con matchmaking",
    desc: "Tu propiedad llega solo a compradores con 70%+ de compatibilidad psicográfica. Menos visitas, más cierres.",
  },
  {
    icon: Crown,
    title: "Master Partner local",
    desc: "Un líder inmobiliario certificado en tu ciudad coordina la operación con agentes especializados de su red.",
  },
  {
    icon: Users,
    title: "Agentes inmobiliarios profesionales",
    desc: "Recibe asesoría personalizada de agentes capacitados que te guían en cada paso de la negociación.",
  },
  {
    icon: ShieldCheck,
    title: "Certeza legal garantizada",
    desc: "Cálculo notarial, bóveda de documentos cifrada y contratos validados. Vende protegido y sin sorpresas.",
  },
  {
    icon: TrendingUp,
    title: "Valor real de mercado",
    desc: "Valuación MVIP con IA basada en comparables reales. Vende al mejor precio posible para tu zona.",
  },
  {
    icon: HandshakeIcon,
    title: "Sin membresía mensual",
    desc: "0 pesos para registrarte y publicar. Solo pagas comisión acordada con el Master Partner al cerrar la venta.",
  },
];

const STEPS = [
  { n: "01", title: "Crea tu cuenta gratis", desc: "Sin tarjeta de crédito. 5 minutos." },
  { n: "02", title: "Carga tu propiedad", desc: "Sube fotos y completa los datos. Te apoya el Master Partner." },
  { n: "03", title: "Acepta el acuerdo de comisión", desc: "Negocia directamente con el MP de tu zona. Sin sorpresas." },
  { n: "04", title: "Recibe leads pre-calificados", desc: "Solo contactos con alta intención de compra (70%+ match)." },
];

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#CCD500]">
    <span className="h-px w-8 bg-[#CCD500]/60" /> {children}
  </span>
);

// Valuation Demo Calculator
const ValuationDemo = ({ onCTA }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    city: "", colonia: "", zone: "media",
    type: "casa", area: 200, year: 2015, condition: "bueno", bedrooms: 3,
  });
  const [coloniaMap, setColoniaMap] = useState(FALLBACK_COLONIA_FACTOR);

  // Fetch latest colonias factors from backend (admin-managed)
  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/valuation/colonias`;
    axios.get(url).then((res) => {
      const list = res?.data?.colonias || [];
      if (list.length > 0) {
        const map = {};
        list.forEach((c) => {
          if (c.colonia_key && typeof c.factor === "number") {
            map[c.colonia_key] = c.factor;
          }
        });
        setColoniaMap(map);
      }
    }).catch(() => { /* fallback in state */ });
  }, []);

  const result = useMemo(() => {
    const cityKey = (form.city || "").trim().toLowerCase();
    const coloniaKey = (form.colonia || "").trim().toLowerCase();
    const pricePerM2Base = CITY_PRICE_PER_M2[cityKey] || CITY_PRICE_PER_M2.default;
    const typeF = TYPE_FACTOR[form.type] || 1.0;
    const condF = CONDITION_FACTOR[form.condition] || 1.0;
    const age = Math.max(0, new Date().getFullYear() - Number(form.year || 2015));
    const ageF = AGE_FACTOR(age);
    const zoneF = (ZONE_FACTOR[form.zone] || ZONE_FACTOR.media).factor;
    // Colonia-specific factor (overrides zone if recognized for higher precision)
    const coloniaF = coloniaMap[coloniaKey] || null;
    // Effective location factor: prefer colonia data when available, else use zone
    const locationF = coloniaF || zoneF;
    const pricePerM2 = Math.round(pricePerM2Base * locationF);
    const base = pricePerM2 * Number(form.area || 0) * typeF * condF * ageF;
    return {
      estimate: Math.round(base),
      min: Math.round(base * 0.92),
      max: Math.round(base * 1.10),
      pricePerM2,
      pricePerM2Base,
      locationFactor: locationF,
      coloniaRecognized: !!coloniaF,
      cityKey: cityKey || "tu ciudad",
      zoneLabel: (ZONE_FACTOR[form.zone] || ZONE_FACTOR.media).label,
    };
  }, [form, coloniaMap]);

  const fmt = (n) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : `$${Math.round(n / 1_000)}K`;
  const canCalc = !!form.city && Number(form.area) > 0;

  return (
    <div className="rounded-3xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-6 md:p-8" data-testid="valuation-demo">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-[#CCD500]" />
          <span className="text-xs uppercase tracking-widest text-[#CCD500] font-bold">Paso {step} de 3</span>
        </div>
        <div className="flex gap-1.5">
          {[1, 2, 3].map((n) => (
            <span key={n} className={`h-1.5 w-8 rounded-full ${n <= step ? "bg-[#CCD500]" : "bg-white/10"}`} />
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
            <h3 className="font-display text-2xl font-bold text-white">Ubicación y tipo</h3>
            <p className="text-xs text-slate-400 -mt-2">La zona y la colonia son los factores de mayor peso en el valor final.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Ciudad</label>
                <input
                  type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Querétaro, CDMX, Mérida..." data-testid="val-city-input"
                  className="w-full px-4 h-11 rounded-xl bg-slate-950/50 border border-white/10 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#CCD500]" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block flex items-center gap-1">
                  Colonia <span className="text-[10px] text-[#CCD500]">↑ +precisión</span>
                </label>
                <input
                  type="text" value={form.colonia} onChange={(e) => setForm({ ...form, colonia: e.target.value })}
                  placeholder="Polanco, Providencia, Juriquilla..." data-testid="val-colonia-input"
                  className="w-full px-4 h-11 rounded-xl bg-slate-950/50 border border-white/10 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#CCD500]" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Categoría de zona</label>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {Object.entries(ZONE_FACTOR).map(([key, z]) => (
                  <button key={key} type="button" onClick={() => setForm({ ...form, zone: key })} data-testid={`val-zone-${key}`}
                    title={z.desc}
                    className={`px-2 py-2 rounded-xl text-[11px] font-semibold transition-all border text-left ${form.zone === key ? "bg-[#CCD500] text-black border-[#CCD500]" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
                    <span className="block leading-tight">{z.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Tipo de propiedad</label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {["casa", "departamento", "loft", "town house", "terreno"].map((t) => (
                  <button key={t} type="button" onClick={() => setForm({ ...form, type: t })} data-testid={`val-type-${t}`}
                    className={`px-3 py-2.5 rounded-xl text-xs font-semibold capitalize transition-all border ${form.type === t ? "bg-[#CCD500] text-black border-[#CCD500]" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Superficie construida (m²)</label>
              <input
                type="number" min="20" max="5000" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}
                data-testid="val-area-input"
                className="w-full px-4 h-11 rounded-xl bg-slate-950/50 border border-white/10 text-white focus:outline-none focus:border-[#CCD500]" />
            </div>
            <Button onClick={() => setStep(2)} disabled={!canCalc} data-testid="val-step-next-1" className="w-full bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] rounded-full h-12">
              Continuar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
            <h3 className="font-display text-2xl font-bold text-white">Estado y antigüedad</h3>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Año de construcción</label>
              <input
                type="number" min="1900" max={new Date().getFullYear()} value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="val-year-input"
                className="w-full px-4 h-11 rounded-xl bg-slate-950/50 border border-white/10 text-white focus:outline-none focus:border-[#CCD500]" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Estado de conservación</label>
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                {["excelente", "muy bueno", "bueno", "regular", "necesita reparación"].map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, condition: c })} data-testid={`val-cond-${c.replace(/\s+/g, "-")}`}
                    className={`px-2 py-2.5 rounded-xl text-[11px] font-semibold capitalize transition-all border ${form.condition === c ? "bg-[#CCD500] text-black border-[#CCD500]" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 bg-white/5 text-white border-white/20 hover:bg-white/10 rounded-full h-12">
                <ArrowLeft className="w-4 h-4 mr-2" /> Atrás
              </Button>
              <Button onClick={() => setStep(3)} data-testid="val-step-next-2" className="flex-1 bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] rounded-full h-12">
                Calcular <Calculator className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="s3" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center space-y-4">
            <Sparkles className="w-10 h-10 text-[#CCD500] mx-auto" />
            <p className="text-xs uppercase tracking-widest text-slate-400">Valor estimado de mercado</p>
            <p className="font-display text-5xl font-black text-white" data-testid="val-result">{fmt(result.estimate)}</p>
            <p className="text-sm text-slate-300">
              Rango ±10%: <span className="text-white font-semibold">{fmt(result.min)}</span> – <span className="text-white font-semibold">{fmt(result.max)}</span>
            </p>

            {/* Location signal */}
            <div className="rounded-xl bg-slate-950/60 border border-white/10 p-3 text-left">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400 mb-2">
                <MapPin className="w-3.5 h-3.5 text-[#CCD500]" /> Ajuste por ubicación
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-slate-500">Ciudad</p>
                  <p className="text-white font-semibold capitalize">{result.cityKey}</p>
                </div>
                <div>
                  <p className="text-slate-500">Zona</p>
                  <p className="text-white font-semibold">{result.zoneLabel}</p>
                </div>
                {form.colonia && (
                  <div className="col-span-2">
                    <p className="text-slate-500">Colonia</p>
                    <p className="text-white font-semibold flex items-center gap-1.5 capitalize">
                      {form.colonia}
                      {result.coloniaRecognized && (
                        <span className="text-[10px] bg-[#CCD500]/15 text-[#CCD500] border border-[#CCD500]/30 px-1.5 py-0.5 rounded-full">
                          micro-mercado reconocido
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-2 pt-2 border-t border-white/5 text-[11px] text-slate-400">
                Factor de ubicación aplicado: <span className="text-[#CCD500] font-bold">×{result.locationFactor.toFixed(2)}</span> sobre el precio base de ciudad.
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 pt-2">
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">$/m² ajustado</p>
                <p className="font-display text-base font-bold text-white">${result.pricePerM2.toLocaleString()}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">m²</p>
                <p className="font-display text-base font-bold text-white">{form.area}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Antigüedad</p>
                <p className="font-display text-base font-bold text-white">{Math.max(0, new Date().getFullYear() - Number(form.year))} años</p>
              </div>
            </div>

            {/* Locked / Account-required features */}
            <div className="rounded-xl bg-gradient-to-br from-[#CCD500]/10 to-fuchsia-500/5 border border-[#CCD500]/30 p-4 text-left">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-4 h-4 text-[#CCD500]" />
                <p className="text-[11px] uppercase tracking-widest text-[#CCD500] font-bold">Desbloquea con tu cuenta gratis</p>
              </div>
              <ul className="space-y-2 text-xs text-slate-200">
                <li className="flex items-start gap-2" data-testid="locked-feat-comparables">
                  <BarChart3 className="w-3.5 h-3.5 text-[#CCD500] mt-0.5 shrink-0" />
                  <span><strong className="text-white">30+ comparables reales</strong> de tu colonia con fotos, precio cerrado y días en mercado.</span>
                </li>
                <li className="flex items-start gap-2" data-testid="locked-feat-trend">
                  <LineChart className="w-3.5 h-3.5 text-[#CCD500] mt-0.5 shrink-0" />
                  <span><strong className="text-white">Tendencia de precios 12 meses</strong> de tu zona vs el promedio de la ciudad.</span>
                </li>
                <li className="flex items-start gap-2" data-testid="locked-feat-time">
                  <Clock className="w-3.5 h-3.5 text-[#CCD500] mt-0.5 shrink-0" />
                  <span><strong className="text-white">Tiempo estimado de venta</strong> según rotación real de tu colonia.</span>
                </li>
                <li className="flex items-start gap-2" data-testid="locked-feat-pdf">
                  <FileSignature className="w-3.5 h-3.5 text-[#CCD500] mt-0.5 shrink-0" />
                  <span><strong className="text-white">Dictamen MVIP en PDF firmado</strong> útil para notarías y compradores.</span>
                </li>
                <li className="flex items-start gap-2" data-testid="locked-feat-mp">
                  <Crown className="w-3.5 h-3.5 text-[#CCD500] mt-0.5 shrink-0" />
                  <span><strong className="text-white">Asesoría del Master Partner</strong> de tu ciudad para fijar precio anclaje y estrategia.</span>
                </li>
                <li className="flex items-start gap-2" data-testid="locked-feat-terreno">
                  <Building2 className="w-3.5 h-3.5 text-[#CCD500] mt-0.5 shrink-0" />
                  <span><strong className="text-white">Módulo Terreno</strong>: si es lote, conoce potencial de desarrollo y uso de suelo.</span>
                </li>
              </ul>
            </div>

            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-200 text-left">
              ⓘ Estimación referencial. La valuación MVIP profesional incluye visita virtual, hedónica con 200+ variables y validación notarial — disponible al crear tu cuenta gratis.
            </div>
            <Button onClick={onCTA} data-testid="val-final-cta" className="w-full bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] rounded-full h-12">
              Crear cuenta y desbloquear MVIP completo <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <button onClick={() => setStep(1)} className="text-xs text-slate-400 hover:text-white">Recalcular con otros datos</button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const SellerLandingPage = () => {
  const navigate = useNavigate();
  const goRegister = () => navigate("/auth?role=owner");

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 font-body overflow-x-hidden selection:bg-[#CCD500] selection:text-black">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B1120]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="sl-back-btn" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Volver al inicio</span>
          </button>
          <img src="https://customer-assets.emergentagent.com/job_propswipe-7/artifacts/65kacgp6_Isotipo%20Findperty-2.png" alt="Findperty" className="h-8 brightness-0 invert" />
          <Button onClick={goRegister} className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 transition-all rounded-full px-5" data-testid="sl-header-cta">
            Vender mi propiedad
          </Button>
        </div>
      </header>

      {/* Breadcrumbs */}
      <div className="pt-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Breadcrumbs items={[{ label: "Vende tu Propiedad" }]} />
        </div>
      </div>

      {/* Hero */}
      <section className="relative py-16 md:py-20 px-6 lg:px-8 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[#CCD500]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-20 -right-40 w-[500px] h-[500px] bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-6">
            <Eyebrow>Para Propietarios</Eyebrow>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mt-6" data-testid="sl-hero-title">
              Vende con{" "}
              <span className="bg-gradient-to-r from-[#CCD500] via-[#E5FF00] to-[#CCD500] bg-clip-text text-transparent">certeza legal</span>{" "}
              y al mejor precio.
            </h1>
            <p className="mt-7 text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl">
              Exposición dirigida con <span className="text-white font-medium">matchmaking psicográfico</span>.
              Tu propiedad llega únicamente a compradores con 70%+ de compatibilidad real, coordinada por un Master Partner local con agentes profesionales.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={goRegister} data-testid="sl-hero-primary-cta"
                className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 transition-all shadow-[0_0_30px_rgba(204,213,0,0.4)] rounded-full px-7 h-14 text-base">
                Cargar mi propiedad gratis
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => document.getElementById("valuation")?.scrollIntoView({ behavior: "smooth" })} data-testid="sl-hero-secondary-cta"
                className="bg-white/5 text-white border-white/20 hover:bg-white/10 hover:border-white/40 rounded-full px-7 h-14 text-base backdrop-blur-md">
                <Calculator className="w-4 h-4 mr-2" />
                Calcular valor de mi propiedad
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Check className="w-4 h-4 text-[#CCD500]" /> Sin tarjeta de crédito
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Check className="w-4 h-4 text-[#CCD500]" /> Sin membresía mensual
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Check className="w-4 h-4 text-[#CCD500]" /> Solo comisión al cerrar
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="lg:col-span-6">
            <div id="valuation" className="scroll-mt-24">
              <div className="text-center mb-5">
                <Eyebrow>Demo Interactivo</Eyebrow>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white mt-3" data-testid="sl-val-title">
                  Calcula el valor de tu propiedad
                </h2>
                <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">
                  Ingresa ciudad, <span className="text-[#CCD500] font-semibold">zona y colonia</span>, tipo y superficie. Nuestro motor MVIP ajusta el precio por micro-mercado en segundos.
                </p>
              </div>
              <ValuationDemo onCTA={goRegister} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="relative py-20 md:py-28 px-6 lg:px-8 bg-gradient-to-b from-transparent via-slate-950/40 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <Eyebrow>Por qué Findperty</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
              La forma <span className="text-[#CCD500]">inteligente</span> de vender tu propiedad.
            </h2>
            <p className="mt-4 text-slate-400">
              Olvídate de visitas innecesarias y portales con leads fríos. Findperty te conecta con compradores comprobadamente compatibles.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="rounded-3xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-6 hover:border-[#CCD500]/40 transition-all"
                data-testid={`sl-benefit-${i}`}>
                <div className="w-11 h-11 rounded-2xl bg-[#CCD500]/10 border border-[#CCD500]/30 flex items-center justify-center mb-4">
                  <b.icon className="w-5 h-5 text-[#CCD500]" />
                </div>
                <h3 className="font-display text-xl font-bold text-white mb-2">{b.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS - 4 PASOS */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <Eyebrow>Proceso simple</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">4 pasos para vender mejor.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="relative rounded-2xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-6">
                <span className="font-display text-3xl font-black text-[#CCD500]/30 absolute top-3 right-4">{s.n}</span>
                <h3 className="font-display text-base font-bold text-white mt-2 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MODEL DE COMISIÓN */}
      <section className="relative py-20 px-6 lg:px-8 bg-gradient-to-b from-transparent via-slate-950/40 to-transparent">
        <div className="max-w-5xl mx-auto rounded-3xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-8 md:p-12">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <Eyebrow>Modelo Transparente</Eyebrow>
              <h2 className="font-display text-3xl md:text-4xl font-bold mt-4 tracking-tight">
                <span className="text-[#CCD500]">$0</span> por registrarte. <br />
                Comisión solo al vender.
              </h2>
              <p className="mt-5 text-slate-400 leading-relaxed">
                Findperty no cobra membresía mensual ni cuota por publicar. Acuerdas una comisión directamente con el Master Partner de tu zona, que cubre la asesoría legal completa, el matchmaking psicográfico y la coordinación de agentes profesionales.
              </p>
              <div className="mt-6 space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-200">
                  <Check className="w-4 h-4 text-[#CCD500]" /> Cargar propiedad: <span className="font-bold text-[#CCD500] ml-1">Gratis</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-200">
                  <Check className="w-4 h-4 text-[#CCD500]" /> Valuación MVIP profesional: <span className="font-bold text-[#CCD500] ml-1">Gratis</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-200">
                  <Check className="w-4 h-4 text-[#CCD500]" /> Acceso a Master Partner local: <span className="font-bold text-[#CCD500] ml-1">Gratis</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-200">
                  <HandshakeIcon className="w-4 h-4 text-[#CCD500]" /> Comisión: <span className="ml-1">acordada directamente con el Master Partner</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl bg-gradient-to-br from-[#CCD500]/10 to-transparent border border-[#CCD500]/30 p-6 text-center">
                <CreditCard className="w-8 h-8 text-[#CCD500] mx-auto mb-3" />
                <p className="font-display text-3xl font-black text-white">Sin tarjeta</p>
                <p className="text-xs uppercase tracking-widest text-slate-400 mt-1">para registrarte</p>
                <div className="my-4 border-t border-white/10" />
                <p className="font-display text-3xl font-black text-white">$0/mes</p>
                <p className="text-xs uppercase tracking-widest text-slate-400 mt-1">de membresía</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="px-6 lg:px-8 pb-24">
        <div className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-[#CCD500] to-[#9DAB00] p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/20 blur-3xl" />
          <div className="relative">
            <Home className="w-10 h-10 text-black mx-auto mb-4" />
            <h2 className="font-display text-4xl md:text-6xl font-black text-black tracking-tighter mb-4 leading-[0.95]">
              ¿Listo para vender?
            </h2>
            <p className="text-black/70 text-lg max-w-xl mx-auto mb-8">
              Crea tu cuenta gratis y carga tu propiedad en minutos. Sin tarjeta de crédito, sin compromiso.
            </p>
            <Button size="lg" onClick={goRegister} data-testid="sl-final-cta"
              className="bg-black text-[#CCD500] font-bold hover:bg-slate-900 hover:scale-105 rounded-full px-8 h-14 text-base">
              Cargar mi propiedad
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-xs text-black/60 mt-6">Sin tarjeta de crédito • Setup en 5 minutos • Sin membresía</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 lg:px-8 py-10">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-500">
          © 2026 Findperty · Vende con certeza y al mejor precio
        </div>
      </footer>
    </div>
  );
};

export default SellerLandingPage;
